import { escapeJsonPointer } from '../json/escape-json-pointer.js';
import { Queue } from '../queue/queue.js';
/**
 * Traverses an object or array, returning a deep copy in which circular references are replaced
 * by JSON Reference objects of the form: `{ $ref: "#/path/to/original" }`.
 * This allows safe serialization of objects with cycles, following the JSON Reference convention (RFC 6901).
 * An optional `prefix` for the `$ref` path can be provided via options.
 *
 * @param obj - The input object or array to process
 * @param options - Optional configuration; you can set a prefix for $ref pointers
 * @returns A new object or array, with all circular references replaced by $ref pointers
 */
export const toJsonCompatible = (obj, options = {}) => {
    const { prefix = '', cache = new WeakMap() } = options;
    const toRef = (path) => ({ $ref: `#${path ?? ''}` });
    // Primitives and null are returned as-is
    if (typeof obj !== 'object' || obj === null) {
        return obj;
    }
    const rootPath = prefix;
    cache.set(obj, rootPath);
    const rootResult = Array.isArray(obj) ? new Array(obj.length) : {};
    const queue = new Queue();
    queue.enqueue({ node: obj, result: rootResult, path: rootPath });
    while (!queue.isEmpty()) {
        const frame = queue.dequeue();
        if (!frame) {
            continue;
        }
        const { node, result, path } = frame;
        // Handle arrays (preserve sparse arrays like Array#map does)
        if (Array.isArray(node)) {
            const input = node;
            const out = result;
            for (let index = 0; index < input.length; index++) {
                if (!(index in input)) {
                    continue;
                }
                const item = input[index];
                const itemPath = `${path}/${index}`;
                if (typeof item !== 'object' || item === null) {
                    out[index] = item;
                    continue;
                }
                const existingPath = cache.get(item);
                if (existingPath !== undefined) {
                    out[index] = toRef(existingPath);
                    continue;
                }
                cache.set(item, itemPath);
                const childResult = Array.isArray(item) ? new Array(item.length) : {};
                out[index] = childResult;
                queue.enqueue({ node: item, result: childResult, path: itemPath });
            }
            continue;
        }
        // Handle objects - create a new object with processed values
        const out = result;
        const entries = Object.entries(node);
        for (const [key, value] of entries) {
            const valuePath = `${path}/${escapeJsonPointer(key)}`;
            if (typeof value !== 'object' || value === null) {
                out[key] = value;
                continue;
            }
            const existingPath = cache.get(value);
            if (existingPath !== undefined) {
                out[key] = toRef(existingPath);
                continue;
            }
            cache.set(value, valuePath);
            const childResult = Array.isArray(value) ? new Array(value.length) : {};
            out[key] = childResult;
            queue.enqueue({ node: value, result: childResult, path: valuePath });
        }
    }
    return rootResult;
};
